import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
  Alert,
} from '@mui/material';
import { apiAcceptLegalDocuments } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const LegalAcceptance: React.FC = () => {
  const { user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const versions = user?.legalCurrentVersions;

  const onSubmit = async () => {
    if (!agree) {
      setError('You must agree to continue.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await apiAcceptLegalDocuments(versions);
      await refreshSession();
      navigate('/', { replace: true });
    } catch (e: any) {
      setError(e?.message || 'Could not record your acceptance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto', py: { xs: 4, sm: 6 } }}>
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Updated Legal Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We updated one or more legal documents. Please review and accept the latest versions to continue using Dragonsframe.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <Link href="/terms" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>Terms of Service</Link>
            {' · '}
            <Link href="/privacy" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>Privacy Policy</Link>
            {' · '}
            <Link href="/cookies" underline="hover" sx={{ color: 'primary.main', fontWeight: 600 }}>Cookie Policy</Link>
          </Typography>

          {versions && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Current versions: Terms {versions.termsVersion}, Privacy {versions.privacyVersion}, Cookies {versions.cookieVersion}
            </Typography>
          )}

          <FormControlLabel
            control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
            label="I agree to the Terms of Service, Privacy Policy, and Cookie Policy."
          />

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            disabled={saving}
            onClick={onSubmit}
          >
            {saving ? 'Saving...' : 'Agree and Continue'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LegalAcceptance;
